import { useMemo } from "react";

import {
  TypedDocumentNode,
  UseFragmentOptions,
  UseFragmentResult,
  useFragment as useFragment_experimental,
} from "@apollo/client";
import { DeepPartial } from "@apollo/client/utilities/types/DeepPartial";
import { print, visit } from "graphql";
import { DocumentNode } from "graphql/language/ast";
import { isEmpty } from "lodash";

export default function useFragment<TData = any, TVars = unknown>(
  document: TypedDocumentNode<TData, TVars>,
  from: UseFragmentOptions<TData, TVars>["from"],
  options?: Partial<UseFragmentOptions<TData, TVars>>
): [TData | undefined, UseFragmentResult<TData>] {
  const opts = {
    ...options,
    fragmentName:
      options?.fragmentName ??
      getTopLevelFragmentName(document as DocumentNode),
  };

  // Handle graphql documents that are not yet generated by graphql:gen
  if (isEmpty(document)) {
    return [undefined, { complete: false, data: {} as DeepPartial<TData> }];
  }

  const result = useFragment_experimental({
    ...opts,
    from,
    fragment: document,
  });

  const data =
    result.data == null || isEmpty(result.data) || !result.complete
      ? undefined
      : result.data;

  if (!result.complete && import.meta.env.DEV) {
    if (
      typeof result.missing === "string" &&
      result.missing.startsWith("Dangling reference")
    ) {
      // undefined `id` might be an expected logic of the component, so this is
      // only info level.
      console.warn(
        `[useFragment] ${result.missing}. This could be because \`id\` of the object is undefined.`
      );
    } else {
      const variables = getVariablesUsed(document as DocumentNode);
      console.warn(
        "[useFragment] Fragment data is undefined because it is not complete.\n\n",
        `Fragment: ${print(document)}\n\n`,
        `Missing: ${JSON.stringify(result.missing, null, 2)}\n\n`,
        variables.length > 0
          ? `Some variables are used in this fragment: [${variables}]. Make sure they are passed in the third argument.\n\n`
          : ""
      );
    }
  }

  const returned = useMemo(() => [data, result], [result]);

  return returned as [TData | undefined, UseFragmentResult<TData>];
}

export { useFragment };

function getTopLevelFragmentName(document: DocumentNode): string {
  return (document.definitions?.[0] as any)?.name.value;
}

function getVariablesUsed(document: DocumentNode): string[] {
  const variables: string[] = [];
  visit(document, {
    enter(node) {
      if ("arguments" in node) {
        node.arguments?.forEach((arg) => {
          if ("value" in arg && arg.value.kind == "Variable") {
            variables.push(arg.value.name.value);
          }
        });
      }
    },
  });

  return variables;
}
